import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError, fetchBusinessStatus } from '@srichaitra/api';
import { errorMessageFor } from '@srichaitra/constants';

/**
 * The business dashboard's figures.
 *
 * Which figures arrive depends on who is asking, and the decision is the
 * server's. `finance.businessStatus` reports sales, spending, investment and
 * profit but answers only a built-in role holding the admin tile — Super Admin.
 * Everyone else gets `finance.summary`, which scopes payable and receivable to
 * their own records and omits the cash position unless their role has the Cash
 * In tile.
 *
 * This slice therefore stores a `scope` alongside the data: 'full' when the
 * whole position was returned, 'limited' otherwise. The page renders from that
 * rather than re-deriving the user's privileges, so there is one answer to
 * "what may this person see" and it came from the server.
 */

const initialState = {
  status: 'idle',
  scope: null,
  data: null,
  error: null
};

function asRejection(error) {
  if (error instanceof ApiError) return { code: error.code, message: error.userMessage };
  return { code: 'UNKNOWN_ERROR', message: errorMessageFor('UNKNOWN_ERROR') };
}

/**
 * Loads the business figures, if this account is entitled to them.
 *
 * `canSeeBusinessStatus` is passed in by the caller from auth state rather than
 * read here, so this thunk has no opinion about authorisation — it just skips
 * the request when the signed-in role plainly does not qualify, rather than
 * firing one that is certain to be refused.
 *
 * A FORBIDDEN is recorded as 'limited', not as a failure. The client's idea of
 * who qualifies is a convenience; the server's is the truth, and when the two
 * disagree the user should be told the figures are restricted rather than shown
 * an error suggesting something broke.
 */
export const loadDashboard = createAsyncThunk(
  'dashboard/load',
  async ({ canSeeBusinessStatus } = {}, { rejectWithValue }) => {
    if (!canSeeBusinessStatus) return { scope: 'limited', data: null };

    try {
      return { scope: 'full', data: await fetchBusinessStatus() };
    } catch (error) {
      if (error instanceof ApiError && error.code === 'FORBIDDEN') {
        return { scope: 'limited', data: null };
      }
      return rejectWithValue(asRejection(error));
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    /** Clears the figures on sign-out, so the next account never sees the last one's. */
    clearDashboard() {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadDashboard.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(loadDashboard.fulfilled, (state, action) => {
        state.status = 'ready';
        state.scope = action.payload.scope;
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(loadDashboard.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? asRejection(null);
      });
  }
});

export const { clearDashboard } = dashboardSlice.actions;
export const dashboardReducer = dashboardSlice.reducer;

/* --- Selectors --------------------------------------------------------- */

export const selectDashboardStatus = (state) => state.dashboard.status;
export const selectDashboardScope = (state) => state.dashboard.scope;
export const selectDashboardError = (state) => state.dashboard.error;

/**
 * The figures the dashboard renders, flattened out of the server's shape.
 *
 * Returns null when there are none — either nothing has loaded yet, or the role
 * is not entitled to them. The panel checks `scope` before drawing anything, so
 * a restricted account is told the figures are withheld rather than shown a
 * chart of zeroes.
 *
 * `investment` is derived here rather than in the component: own money plus
 * borrowed money is a definition, and definitions belong next to the data.
 */
export const selectDashboardFigures = (state) => {
  const { data, scope } = state.dashboard;
  if (!data) return null;

  return {
    scope,
    sales: data.profit?.revenue,
    stockSpending: data.profit?.costOfStockSold,
    miscSpending: data.profit?.miscExpenses,
    profit: data.profit?.amount,

    investment: Number(data.cashIn?.self ?? 0) + Number(data.cashIn?.other ?? 0),
    investmentSelf: data.cashIn?.self,
    investmentOther: data.cashIn?.other,

    payable: data.payable,
    receivable: data.receivable,
    cashInHand: data.cashIn?.total
  };
};
